/**
 * useProposalListKpis - KPIs totalizadores da listagem de Propostas Geradas
 *
 * Consome a RPC `get_proposal_list_kpis`, que aplica exatamente os mesmos
 * filtros e regras de visibilidade da listagem paginada.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 2 — Listagem de Propostas)
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ProposalFilters } from '@/hooks/useProposalKanbanColumn';

/** Logger condicional centralizado */
const logger = createLogger('useProposalListKpis');

/** Estrutura dos KPIs retornados pela RPC */
export interface ProposalListKpis {
  total_proposals: number;
  created_today: number;
  sent_count: number;
  accepted_count: number;
  rejected_count: number;
  total_amount: number;
  accepted_amount: number;
}

/** Valores padrão (zerados) */
const EMPTY_KPIS: ProposalListKpis = {
  total_proposals: 0,
  created_today: 0,
  sent_count: 0,
  accepted_count: 0,
  rejected_count: 0,
  total_amount: 0,
  accepted_amount: 0,
};

/**
 * Busca os totalizadores da listagem de propostas.
 *
 * @param filters - Filtros ativos da listagem
 */
export function useProposalListKpis(filters?: ProposalFilters) {
  const { currentTenant } = useAuth();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['proposal-list-kpis', currentTenant?.id, filters],
    queryFn: async () => {
      logger.debug('Buscando KPIs da listagem de propostas', { filters });

      const { data, error } = await supabase.rpc('get_proposal_list_kpis', {
        p_tenant_hint: currentTenant?.id || null,
        p_workspace_id: filters?.workspace_id || null,
        p_status: filters?.status || null,
        p_search: filters?.search || null,
        p_seller_id: filters?.seller_id || null,
        p_assigned_to: filters?.assigned_to || null,
        p_proposal_type_id: filters?.proposal_type_id || null,
        p_pipeline_id: filters?.pipeline_id || null,
        p_phase_id: filters?.phase_id || null,
        p_deal_id: filters?.deal_id || null,
        p_created_by: filters?.created_by || null,
        p_date_field: filters?.date_field || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        p_min_total: filters?.min_total ?? null,
        p_max_total: filters?.max_total ?? null,
        p_min_mrr: filters?.min_mrr ?? null,
        p_max_mrr: filters?.max_mrr ?? null,
        p_pdf_status: filters?.pdf_status || null,
        p_public_enabled: filters?.public_enabled ?? null,
        p_viewed: filters?.viewed ?? null,
        p_tag_ids: filters?.tag_ids || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar KPIs de propostas', error);
        throw error;
      }

      // A RPC retorna uma única linha
      const row: any = Array.isArray(data) ? data[0] : data;
      if (!row) return EMPTY_KPIS;

      return {
        total_proposals: Number(row.total_proposals) || 0,
        created_today: Number(row.created_today) || 0,
        sent_count: Number(row.sent_count) || 0,
        accepted_count: Number(row.accepted_count) || 0,
        rejected_count: Number(row.rejected_count) || 0,
        total_amount: Number(row.total_amount) || 0,
        accepted_amount: Number(row.accepted_amount) || 0,
      } as ProposalListKpis;
    },
    enabled: !!currentTenant?.id,
    staleTime: 15_000,
    gcTime: 2 * 60_000,
  });

  return {
    kpis: data || EMPTY_KPIS,
    isLoading,
    refetch,
  };
}
